/**
 * InvoiceSuccessDialog — minimal confirmation after a sale.
 * Print actions hand off to useInvoicePrint (Phase 5).
 */

import { useFeedbackPopup } from '@/hooks/useFeedbackPopup';
import { useEffect, useRef, useState } from 'react';
import { Check, Printer, FileText, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { formatCurrency } from '@/utils/format';
import { useInvoicePrint } from '@/printing';
import { useSettingsStore } from '@/store/useSettingsStore';
import { sendReceipt } from '@/billing/sendReceipt';
import { BILL_DOCUMENT_SETTING, resolveBillDocument } from '@/printing/billDocument';

interface Props {
  invoiceId: string;
  invoiceNumber: string;
  totalAmount: number;
  /** Prefills the "send receipt" field when a customer was attached. */
  customerPhone?: string | null;
  onClose: () => void;
}

/** Hands-free auto-advance for high-volume counters. Any key (other than the
 *  Enter that starts the sale) or a print action cancels it. */
const AUTO_DISMISS_SECONDS = 6;

export function InvoiceSuccessDialog({
  invoiceId,
  invoiceNumber,
  totalAmount,
  customerPhone,
  onClose,
}: Props) {
  const { isPrinting, print80mm, printSystem } = useInvoicePrint();
  // The shop's own bill comes first; the other stays one tap away.
  const billDoc = useSettingsStore((s) => resolveBillDocument(s.getSetting(BILL_DOCUMENT_SETTING)));
  const paperWidth = useSettingsStore((s) => s.getSetting('THERMAL_PRINTER_PAPER_WIDTH') || '80');
  const [status, setStatus] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  // Print and send outcomes pop up like every other message (the dialog
  // closes itself a few seconds later; a popup outlives it).
  useFeedbackPopup(status?.text, status?.tone === 'ok' ? 'success' : 'error', () => setStatus(null));
  const [countdown, setCountdown] = useState<number | null>(AUTO_DISMISS_SECONDS);
  const [phone, setPhone] = useState(customerPhone ?? '');
  const phoneValid = phone.replace(/\D/g, '').length >= 10;

  // Ref so the countdown effect doesn't re-run when the parent re-creates onClose.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const stopCountdown = () => setCountdown(null);

  // Tick the auto-dismiss; at zero, start the next sale.
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      onCloseRef.current();
      return;
    }
    const t = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // Any key other than Enter means the cashier wants to stay (e.g. to print),
  // so cancel the auto-dismiss. Enter is left to activate the focused "New
  // sale" button (muscle memory: Enter starts the next sale).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') setCountdown(null);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  const handleThermal = async () => {
    stopCountdown();
    setStatus(null);
    const res = await print80mm(invoiceId);
    setStatus(
      res.ok
        ? { tone: 'ok', text: 'Sent to printer.' }
        : { tone: 'err', text: res.message || 'Print failed' },
    );
  };

  const handleA4 = async () => {
    stopCountdown();
    setStatus(null);
    const res = await printSystem(invoiceId);
    setStatus(
      res.ok
        ? { tone: 'ok', text: res.message || 'Sent to printer.' }
        : { tone: 'err', text: res.message || 'Print failed' },
    );
  };

  const receiptButton = (
    <Button key="receipt" variant="secondary" block onClick={handleThermal} disabled={isPrinting} type="button">
      <Printer size={14} /> Receipt<span className="hidden sm:inline"> · {paperWidth} mm</span>
    </Button>
  );
  const a4Button = (
    <Button key="a4" variant="secondary" block onClick={handleA4} disabled={isPrinting} type="button"
      title="Print, or choose “Save as PDF” in the print dialog">
      <FileText size={14} /> A4 invoice
    </Button>
  );

  const [sending, setSending] = useState(false);
  const handleSend = async () => {
    if (!phoneValid || sending) return;
    stopCountdown();
    setStatus(null);
    setSending(true);
    const res = await sendReceipt(invoiceId, phone.trim());
    setSending(false);
    if (res.ok) {
      const via = res.channel === 'sms' ? 'SMS (not on WhatsApp)' : 'WhatsApp';
      setStatus({ tone: 'ok', text: `Receipt sent via ${via}${res.dryRun ? ' · dry-run' : ''}.` });
    } else {
      setStatus({ tone: 'err', text: res.reason || 'Could not send the receipt' });
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 px-6 py-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/15 text-success-ink">
        <Check size={26} strokeWidth={2.5} />
      </div>
      <div>
        <div className="text-heading text-fg-primary">{invoiceNumber}</div>
        <div className="mt-1 text-display text-fg-primary tabular-nums">
          {formatCurrency(totalAmount)}
        </div>
      </div>
      <p className="text-body text-fg-secondary">Saved offline. Will sync automatically.</p>

      <div className="w-full space-y-2 pt-2">
        {/* Digital receipt — type/confirm the number, one tap to send (no pop-up) */}
        <div className="flex w-full items-stretch gap-2">
          <Input
            type="tel"
            inputMode="numeric"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSend();
              }
            }}
            placeholder="Customer WhatsApp / mobile"
            aria-label="Customer WhatsApp or mobile number"
            className="min-w-0 flex-1 text-left"
          />
          <Button onClick={handleSend} disabled={sending || !phoneValid} type="button">
            <MessageCircle size={14} /> {sending ? 'Sending…' : 'Send'}
          </Button>
        </div>
        {/* Print options */}
        <div className="flex w-full gap-2">
          {billDoc === 'a4' ? [a4Button, receiptButton] : [receiptButton, a4Button]}
        </div>
        <Button block size="lg" onClick={onClose} autoFocus>
          New sale
        </Button>
      </div>
      <p className="text-label text-fg-tertiary" aria-live="polite">
        {countdown !== null
          ? `Starting new sale in ${countdown}s · press any key to stay`
          : 'Press Enter to start a new sale'}
      </p>
    </div>
  );
}
